import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Users, Plus, Search, Edit, Trash2, Shield, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import api from '../services/api';
import './UserList.css';

/** Генерирует массив номеров страниц с «...» для пагинации */
function buildPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, 2, total - 1, total]);
  for (let d = -1; d <= 1; d++) {
    const p = current + d;
    if (p >= 1 && p <= total) pages.add(p);
  }
  const sorted = [...pages].sort((a, b) => a - b);
  const result = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push('...');
    result.push(sorted[i]);
  }
  return result;
}

function UserList() {
  const { t, i18n } = useTranslation(['admin', 'common']);
  const isRu = i18n.language === 'ru';
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [departments, setDepartments] = useState([]);
  const [deptFilter, setDeptFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    fetchDepartments();
    fetchUsers();
  }, []);

  const fetchDepartments = async () => {
    try {
      const res = await api.getDepartments();
      setDepartments(res.data.results || res.data);
    } catch (err) {
      console.error('Error loading departments:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (roleFilter) params.role = roleFilter;
      if (activeFilter !== '') params.is_active = activeFilter;
      if (deptFilter) params.department = deptFilter;

      const res = await api.getUsers(params);
      setUsers(res.data.results || res.data);
      setLoading(false);
    } catch (err) {
      console.error('Error loading users:', err);
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(1);
    const timer = setTimeout(() => fetchUsers(), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, roleFilter, activeFilter, deptFilter]);

  // Пагинация
  const totalItems = users.length;
  const totalPages = pageSize === 0 ? 1 : Math.ceil(totalItems / pageSize);
  const displayedUsers = useMemo(() => {
    if (pageSize === 0) return users;
    const start = (currentPage - 1) * pageSize;
    return users.slice(start, start + pageSize);
  }, [users, currentPage, pageSize]);
  const pageNumbers = useMemo(() => buildPageNumbers(currentPage, totalPages), [currentPage, totalPages]);

  const handleDelete = async (id, username) => {
    if (!window.confirm(t('admin:users.deleteConfirm', { username }))) return;
    try {
      await api.deleteUser(id);
      fetchUsers();
    } catch (err) {
      alert(t('admin:users.deleteError') + ': ' + (err.response?.data?.detail || err.message));
    }
  };

  return (
    <div className="user-list-page">
      <div className="user-list-header">
        <div>
          <h1><Users size={24} /> {t('admin:users.title')}</h1>
          <p className="subtitle">{t('admin:users.subtitle')}</p>
        </div>
        <button className="btn-primary" onClick={() => navigate('/users/create')}>
          <Plus size={18} /> {t('admin:users.createUser')}
        </button>
      </div>

      {/* Фильтры */}
      <div className="user-filters">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder={t('admin:users.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
          <option value="">{t('admin:users.allRoles')}</option>
          {['admin', 'director', 'deputy_director', 'department_head', 'employee', 'analyst'].map(key => (
            <option key={key} value={key}>{t(`common:roles.${key}`)}</option>
          ))}
        </select>
        <select value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)}>
          <option value="">{t('admin:users.allDepartments')}</option>
          {departments.map(d => (
            <option key={d.id} value={d.id}>{isRu ? d.name_ru : d.name_uz}</option>
          ))}
        </select>
        <select value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)}>
          <option value="">{t('admin:users.allStatuses')}</option>
          <option value="true">{t('admin:users.activeUsers')}</option>
          <option value="false">{t('admin:users.inactiveUsers')}</option>
        </select>
      </div>

      {/* Панель пагинации сверху */}
      <div className="pagination-bar">
        <div className="pagination-info">
          <span>{isRu ? 'Показать:' : "Ko'rsatish:"}</span>
          <select
            className="page-size-select"
            value={pageSize}
            onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
          >
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
            <option value={0}>{isRu ? 'Все' : 'Hammasi'}</option>
          </select>
          <span className="pagination-total">
            {isRu ? `Найдено: ${totalItems}` : `Topildi: ${totalItems}`}
          </span>
        </div>
      </div>

      {/* Таблица */}
      {loading ? (
        <div className="loading-spinner">{t('common:table.loading')}</div>
      ) : (
        <div className="user-table-wrapper">
          <table className="user-table">
            <thead>
              <tr>
                <th>{t('admin:users.fullName')}</th>
                <th>{t('admin:users.login')}</th>
                <th>{t('admin:users.role')}</th>
                <th>{t('admin:users.department')}</th>
                <th>{t('admin:users.position')}</th>
                <th>{t('common:table.status')}</th>
                <th>{t('common:table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {displayedUsers.length === 0 ? (
                <tr><td colSpan="7" className="empty-cell">{t('admin:users.noUsers')}</td></tr>
              ) : (
                displayedUsers.map((u, idx) => (
                  <tr key={u.id}>
                    <td className="user-name-cell">
                      <span className="user-avatar">
                        {(u.last_name || u.username || '?')[0].toUpperCase()}
                      </span>
                      <span>{u.full_name || u.username}</span>
                    </td>
                    <td>{u.username}</td>
                    <td>
                      <span className={`role-badge role-${u.role}`}>
                        {t(`common:roles.${u.role}`, { defaultValue: u.role })}
                      </span>
                    </td>
                    <td>{(isRu ? u.department_name : u.department_name_uz) || '—'}</td>
                    <td>{u.position || '—'}</td>
                    <td>
                      <span className={`status-dot ${u.is_active ? 'active' : 'inactive'}`}>
                        {u.is_active ? t('admin:users.isActive') : t('admin:users.inactive')}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="btn-icon" title={t('common:buttons.edit')} onClick={() => navigate(`/users/${u.id}/edit`)}>
                          <Edit size={16} />
                        </button>
                        <button className="btn-icon btn-icon-danger" title={t('common:buttons.delete')} onClick={() => handleDelete(u.id, u.username)}>
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {/* Пагинация */}
          {totalPages > 1 && (
            <div className="pagination-controls">
              <button className="pagination-btn" disabled={currentPage <= 1} onClick={() => setCurrentPage(1)}>
                <ChevronsLeft size={16} />
              </button>
              <button className="pagination-btn" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)}>
                <ChevronLeft size={16} />
              </button>
              {pageNumbers.map((p, i) =>
                p === '...' ? (
                  <span key={`dots-${i}`} className="pagination-dots">...</span>
                ) : (
                  <button
                    key={p}
                    className={`pagination-btn ${p === currentPage ? 'active' : ''}`}
                    onClick={() => setCurrentPage(p)}
                  >
                    {p}
                  </button>
                )
              )}
              <button className="pagination-btn" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                <ChevronRight size={16} />
              </button>
              <button className="pagination-btn" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(totalPages)}>
                <ChevronsRight size={16} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default UserList;
